import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import BookingLayout from 'pages/booking/BookingLayout';
import { BOOKING_DETAIL_PATH, BOOKING_HOME_PATH } from 'routes/paths';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import { formatLocaleDate, formatLocaleDateTime, formatLocaleTime } from 'lang/date-time';
import IconArrowLeft from 'assets/arrow-narrow-left.svg';
import 'pages/booking/BookingThankyou.css';

/**
 * Confirmation page shown after a successful reservation.
 * Displays a booking summary.
 */
export default function BookingThankyou() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { state } = useLocation();
  const { getAction, jwt } = useApi();
  const { _t, lang } = useLang();
  const [booking, setBooking] = useState<any>(
    () => state || JSON.parse(sessionStorage.getItem('bookingConfirmation') ?? 'null') || {}
  );
  const isAccepted = booking.reservation?.status === 'accepted';
  const isPending = booking.reservation?.status === 'created';
  const isWaitingForPayment = booking.reservation?.status === 'waiting-for-payment';
  const isPaymentFailed = booking.reservation?.status === 'payment-failed';
  const isPaymentReviewRequired = booking.reservation?.status === 'payment-review-required';
  const payment = booking.payment;
  const isGuest = booking.isGuest === true || Boolean(booking.reservation?.guestEmail);
  const reservationId = booking.reservation?.id;
  const paymentGateway = payment?.paymentGateway;
  const paymentStatus = payment?.status;
  const canContinueStripePayment =
    paymentGateway === 'stripe' &&
    isWaitingForPayment &&
    ['initializing', 'waiting', 'processing'].includes(paymentStatus) &&
    Boolean(payment?.attempt?.checkoutUrl);
  const invoiceStatus = payment?.invoice?.issuanceStatus || (payment?.invoice?.invoiceNumber ? 'issued' : undefined);
  const isInvoiceProcessing = invoiceStatus === 'pending';
  const isInvoiceFailed = invoiceStatus === 'failed';
  const formattedBookingDate = booking.slot?.startsAt
    ? formatLocaleDate(booking.slot.startsAt, lang)
    : booking.dateRaw
      ? formatLocaleDate(booking.dateRaw, lang)
      : '';
  const formattedBookingTime =
    booking.slot?.startsAt && booking.slot?.endsAt
      ? `${formatLocaleTime(booking.slot.startsAt, lang)} – ${formatLocaleTime(booking.slot.endsAt, lang)}`
      : '';

  useEffect(() => {
    const paymentAccessToken = sessionStorage.getItem('bookingPaymentAccessToken');
    const paymentNeedsPolling =
      paymentGateway === 'stripe' && ['initializing', 'waiting', 'processing'].includes(paymentStatus);
    if ((!paymentNeedsPolling && invoiceStatus !== 'pending') || (!jwt && !paymentAccessToken)) {
      return undefined;
    }

    let disposed = false;
    /** Polls Phillip's local payment state while background initialization or settlement is pending. */
    const poll = async () => {
      try {
        const response: any = jwt
          ? await getAction('GetReservationPayment')({ parameters: { reservationId } })
          : await getAction('GetPublicReservationPayment')({
              parameters: { accessToken: paymentAccessToken as string },
            });
        if (disposed) return;
        setBooking((current: any) => {
          const next = {
            ...current,
            payment: response.payment,
            reservation: { ...current.reservation, ...(response.reservation || {}) },
          };
          sessionStorage.setItem('bookingConfirmation', JSON.stringify(next));
          return next;
        });
        if (
          paymentStatus === 'initializing' &&
          response.payment?.attempt?.checkoutUrl &&
          sessionStorage.getItem('bookingStripeReturned') !== 'yes'
        ) {
          window.location.assign(response.payment.attempt.checkoutUrl);
        }
      } catch {
        // The durable worker continues reconciliation; keep showing the last known state.
      }
    };

    void poll();
    const interval = window.setInterval(poll, 3000);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [getAction, invoiceStatus, jwt, paymentGateway, paymentStatus, reservationId]);

  return (
    <BookingLayout>
      <div className="thankyou-page">
        <div className="thankyou-inner">
          <div className="thankyou-heading">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle
                cx="14"
                cy="14"
                r="14"
                fill={
                  isPaymentFailed
                    ? '#B42318'
                    : isPending || isWaitingForPayment || isPaymentReviewRequired
                      ? '#D39E00'
                      : '#3CB65A'
                }
              />
              <polyline
                points="8,14 12,18 20,10"
                fill="none"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h1 className="thankyou-title">
              {_t(
                isPaymentFailed
                  ? 'Payment failed'
                  : isPaymentReviewRequired
                    ? 'Payment requires review'
                    : isWaitingForPayment
                      ? 'Reservation awaiting payment'
                      : isPending
                        ? 'Reservation request received'
                        : 'Reservation accepted'
              )}
            </h1>
          </div>

          {booking.service && (
            <div className="thankyou-card">
              <p className="summary-section-label">{_t('Booking summary')}</p>
              <p className="ty-service-name">{booking.service.title}</p>
              {booking.reservation?.id && (
                <div className="summary-stacked-row">
                  <span className="summary-stacked-label">{_t('Reservation number')}</span>
                  <span className="summary-stacked-value">#{booking.reservation.id}</span>
                </div>
              )}
              {formattedBookingDate && (
                <div className="summary-stacked-row">
                  <span className="summary-stacked-label">{_t('Date')}</span>
                  <span className="summary-stacked-value">{formattedBookingDate}</span>
                </div>
              )}
              {formattedBookingTime && (
                <div className="summary-stacked-row">
                  <span className="summary-stacked-label">{_t('Time')}</span>
                  <span className="summary-stacked-value">{formattedBookingTime}</span>
                </div>
              )}
              {booking.reservation?.temporaryPinCode && (
                <div className="summary-stacked-row">
                  <span className="summary-stacked-label">{_t('Temporary PIN')}</span>
                  <span className="pin-value">{booking.reservation.temporaryPinCode}</span>
                  <span className="summary-stacked-label">
                    {_t('Valid from')} {formatLocaleDateTime(booking.reservation.pinValidFrom, lang)} –{' '}
                    {formatLocaleDateTime(booking.reservation.pinValidTo, lang)}
                  </span>
                </div>
              )}
              {!isGuest && ['credit', 'buyable-credits'].includes(booking.service?.paymentMode) && (
                <>
                  <div className="summary-stacked-row">
                    <span className="summary-stacked-label">{_t('Credits charged')}</span>
                    <span className="summary-stacked-value">{booking.reservation?.creditsCharged ?? 0}</span>
                  </div>
                  <div className="summary-stacked-row">
                    <span className="summary-stacked-label">{_t('Credits remaining')}</span>
                    <span className="summary-stacked-value">{booking.reservation?.remainingCredits ?? 0}</span>
                  </div>
                </>
              )}
              {payment && (
                <>
                  {!isGuest && (
                    <div className="summary-stacked-row">
                      <span className="summary-stacked-label">{_t('Credits applied')}</span>
                      <span className="summary-stacked-value">{payment.creditsApplied}</span>
                    </div>
                  )}
                  <div className="summary-stacked-row">
                    <span className="summary-stacked-label">{_t('Paid slots')}</span>
                    <span className="summary-stacked-value">{payment.paidSlots}</span>
                  </div>
                  <div className="summary-stacked-row">
                    <span className="summary-stacked-label">{_t('Amount including VAT')}</span>
                    <span className="summary-stacked-value">
                      {Number(payment.amountWithVat).toFixed(2)} {payment.currencyCode}
                    </span>
                    <span className="summary-stacked-label">
                      {_t('VAT')}: {Number(payment.vat).toFixed(2)} {payment.currencyCode}
                    </span>
                  </div>
                  {payment.paymentGateway === 'manual' &&
                    payment.status === 'waiting' &&
                    Number(payment.amountWithVat) > 0 &&
                    invoiceStatus === 'issued' && (
                      <div className="summary-stacked-row">
                        <span className="summary-stacked-label">{_t('Manual bank payment')}</span>
                        <span className="summary-stacked-value">{payment.invoice.iban}</span>
                        {payment.invoice.bic && (
                          <span className="summary-stacked-label">
                            {_t('BIC')}: {payment.invoice.bic}
                          </span>
                        )}
                        <span className="summary-stacked-label">
                          {_t('Variable symbol')}: {payment.invoice.variableSymbol}
                        </span>
                        <span className="summary-stacked-label">
                          {_t('Due date')}: {formatLocaleDate(payment.invoice.dueDate, lang)}
                        </span>
                        {payment.invoice.payBySquareQr && (
                          <div className="pay-by-square">
                            <span className="summary-stacked-label">{_t('PAY by square')}</span>
                            <img
                              className="pay-by-square-qr"
                              src={payment.invoice.payBySquareQr}
                              alt={_t('PAY by square QR code')}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  {canContinueStripePayment && (
                    <div className="summary-stacked-row">
                      <span className="summary-stacked-label">{_t('Secure card payment')}</span>
                      <button
                        className="thankyou-home-btn"
                        onClick={() => window.location.assign(payment.attempt.checkoutUrl)}
                      >
                        {_t('Continue to payment')}
                      </button>
                    </div>
                  )}
                  {isInvoiceProcessing && (
                    <div className="summary-stacked-row" role="status">
                      <span className="summary-stacked-label">{_t('Invoice is being prepared')}</span>
                      <span className="summary-stacked-value">
                        {_t('Payment details and downloads will appear automatically.')}
                      </span>
                    </div>
                  )}
                  {isInvoiceFailed && (
                    <div className="summary-stacked-row" role="alert">
                      <span className="summary-stacked-label">{_t('Invoice could not be prepared')}</span>
                      <span className="summary-stacked-value">
                        {_t('Your reservation is saved. Please contact support for invoice assistance.')}
                      </span>
                    </div>
                  )}
                  {invoiceStatus === 'issued' && (
                    <div className="summary-stacked-row">
                      <span className="summary-stacked-label">
                        {_t('Invoice')} {payment.invoice.invoiceNumber}
                      </span>
                      <span className="summary-stacked-value">
                        {_t('The invoice PDF has been sent to your email.')}
                      </span>
                    </div>
                  )}
                </>
              )}
              <p className="thankyou-note">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0 }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="8" />
                  <line x1="12" y1="12" x2="12" y2="16" />
                </svg>
                {_t(
                  isPaymentFailed
                    ? 'The payment was not completed. The reservation was released; create a new booking to try again.'
                    : isPaymentReviewRequired
                      ? 'The payment arrived after the reservation ended. No access was created; support will contact you.'
                      : isWaitingForPayment
                        ? payment?.paymentGateway === 'stripe'
                          ? 'Your reservation is held while awaiting card payment confirmation. Access will be issued after payment.'
                          : 'Your reservation is held while awaiting manual payment confirmation. Access will be issued after payment.'
                        : isPending
                          ? 'Your request is awaiting approval. We will notify you by email.'
                          : 'We have sent all booking details to your email.'
                )}
                {isAccepted && Boolean(jwt) && (
                  <>
                    {' '}
                    {_t(
                      'When the reservation begins, you can enter the space using your usual Phillip access methods, such as your RFID card, PIN, or another configured method.'
                    )}
                  </>
                )}
              </p>
            </div>
          )}

          <div className="thankyou-actions">
            <button
              className="thankyou-home-btn thankyou-back-btn"
              onClick={() => navigate(slug ? BOOKING_DETAIL_PATH.insert({ slug }) : BOOKING_HOME_PATH.original)}
            >
              <img src={IconArrowLeft} width={20} height={20} alt="" />
              {_t('Back to home page')}
            </button>
          </div>
        </div>
      </div>
    </BookingLayout>
  );
}
